// ============================================================================
// DYNAMIC PROMPT BUILDER FOR FACET DETECTION
// ============================================================================

import { EXPANDED_FACET_SCHEMA, type FacetTypeDefinition } from './facetSchema'
import { CONTENT_TYPE_MAP, type ContentType } from './types'

/**
 * Build the system prompt for KNOWLEDGE GRAPH extraction
 * Extracts entities, relationships, AND facets
 *
 * IMPORTANT: This prompt prioritizes ENTITIES over facets.
 * The model should extract ALL named entities first, then relationships, then facets.
 */
export function buildSystemPrompt(
  enabledFacets: FacetTypeDefinition[],
  contentType: ContentType
): string {
  const contentTypeInfo = CONTENT_TYPE_MAP.get(contentType)
  const contentTypeHints = contentTypeInfo?.aiHints || ''

  // Simplified facet list - just IDs, not full descriptions
  const facetIds = enabledFacets.map(f => f.id).join(', ')

  return `You are an expert at extracting KNOWLEDGE GRAPHS from business content.

## Content Type: "${contentTypeInfo?.name || contentType}"
${contentTypeHints}

Your PRIMARY task is to identify NAMED ENTITIES and RELATIONSHIPS.
Facets are SECONDARY - they provide supporting evidence.

═══════════════════════════════════════════════════════════════════════════════
ENTITY TYPES TO EXTRACT (extract ALL that appear):
═══════════════════════════════════════════════════════════════════════════════

MARKET DOMAIN:
• Company - Any organization mentioned (THE SUBJECT COMPANY IS CRITICAL)
• Industry - Economic sectors (e.g., "SaaS", "FinTech")
• Competitor - Competing companies or products
• CustomerSegment - Target groups (e.g., "Mid-market CFOs")
• Persona - User archetypes
• Person - Named individuals
• Stakeholder - Groups with decision power
• Problem - Challenges faced
• CustomerPain - Emotional/financial impact
• CustomerGain - Desired outcomes
• CustomerJob - Tasks to accomplish
• Alternative - Current solutions used
• Trend - Market directions
• MarketSignal - Indicators of change

BUSINESS DOMAIN:
• ValueProposition - Value offered
• Channel - How to reach customers
• RevenueStream - How money is earned
• KeyResource - Critical assets
• KeyActivity - Essential actions
• KeyPartnership - Strategic partners
• CostStructure - Major costs
• Strategy - Strategic direction
• StrategicGoal - Business objectives

SOLUTION DOMAIN:
• Product - Products or services
• Feature - Product capabilities
• Requirement - Must-haves
• Constraint - Limitations

═══════════════════════════════════════════════════════════════════════════════
RELATIONSHIPS TO DETECT:
═══════════════════════════════════════════════════════════════════════════════

• Company --targetsSegment--> CustomerSegment
• Company --offersProduct--> Product
• Company --operatesInIndustry--> Industry
• Company --competesAgainst--> Competitor
• Company --usesChannel--> Channel
• Company --hasValueProposition--> ValueProposition
• Company --hasPartnership--> KeyPartnership
• CustomerSegment --hasPain--> CustomerPain
• CustomerSegment --hasJob--> CustomerJob
• CustomerSegment --hasGain--> CustomerGain
• Persona --representsSegment--> CustomerSegment
• Problem --causesPain--> CustomerPain
• Problem --affectsSegment--> CustomerSegment
• ValueProposition --solves--> CustomerPain
• ValueProposition --targets--> CustomerSegment
• Product --addresses--> Problem
• Feature --enables--> CustomerJob
• Alternative --addressesProblem--> Problem
• Trend --impactsIndustry--> Industry
• Channel --reachesSegment--> CustomerSegment

═══════════════════════════════════════════════════════════════════════════════
FACET TYPES (supporting evidence):
═══════════════════════════════════════════════════════════════════════════════

Available types: ${facetIds}

═══════════════════════════════════════════════════════════════════════════════
CRITICAL EXTRACTION RULES:
═══════════════════════════════════════════════════════════════════════════════

1. EXTRACT NAMED ENTITIES: If a company, person, product, or segment is named, create an entity
   - "Sferio helps mid-market CFOs" → Entity: Company "Sferio", Entity: CustomerSegment "Mid-market CFOs"

2. INFER RELATIONSHIPS: Connect entities based on context
   - "Sferio helps mid-market CFOs" → Relationship: Sferio --targetsSegment--> Mid-market CFOs

3. BE COMPREHENSIVE: Extract ALL entities and relationships, even implicit ones
   - "Our LinkedIn ads reach finance professionals" → Channel "LinkedIn ads", CustomerSegment "Finance professionals", Relationship

4. AVOID DUPLICATES: Same entity should be named consistently

Respond in JSON format only.`
}

/**
 * Build the user prompt with the content to analyze
 * Includes the exact JSON schema expected in the response
 */
export function buildUserPrompt(content: string): string {
  return `Extract a KNOWLEDGE GRAPH from this content with entities, relationships, and supporting facets.

CONTENT:
---
${content}
---

═══════════════════════════════════════════════════════════════════════════════
RESPOND WITH THIS EXACT JSON STRUCTURE:
═══════════════════════════════════════════════════════════════════════════════
{
  "entities": [
    {
      "id": "<unique_id like company_1, segment_1>",
      "type": "<Company|Industry|Competitor|CustomerSegment|Persona|Person|Stakeholder|Problem|CustomerPain|CustomerGain|CustomerJob|Alternative|Trend|MarketSignal|ValueProposition|Channel|RevenueStream|KeyResource|KeyActivity|KeyPartnership|CostStructure|Strategy|StrategicGoal|Product|Feature|Requirement|Constraint>",
      "name": "<the actual name or label, e.g. 'Sferio' or 'Mid-market CFOs'>",
      "description": "<brief description>",
      "evidence": "<direct quote from content supporting this entity>",
      "confidence": <0-1>
    }
  ],
  "relationships": [
    {
      "from": "<entity_id>",
      "to": "<entity_id>",
      "type": "<relationship type like targetsSegment, hasPain, offersProduct>",
      "evidence": "<text that supports this relationship>",
      "confidence": <0-1>
    }
  ],
  "facets": [
    {
      "facetTypeId": "<problem|customer_pain|customer_gain|customer_job|workaround|current_behavior|competitor|alternative|trend|decision|opportunity|risk|feature_request|requirement|constraint|quote|observation>",
      "title": "<short title>",
      "content": "<summary of insight>",
      "evidence": "<direct quote>",
      "confidence": <0-1>,
      "suggestedEntityType": "<optional: related entity type>",
      "suggestedReason": "<optional: why>"
    }
  ]
}

CRITICAL: Extract ALL entities first - especially the SUBJECT COMPANY. Every company, person, segment, product mentioned should be an entity. Then create relationships between them. Facets are secondary.

Be comprehensive - it's better to extract more than miss important entities.`
}

/**
 * Group facets by category
 */
function groupFacetsByCategory(
  facets: FacetTypeDefinition[]
): Map<string, FacetTypeDefinition[]> {
  const groups = new Map<string, FacetTypeDefinition[]>()

  for (const facet of facets) {
    const existing = groups.get(facet.category) || []
    existing.push(facet)
    groups.set(facet.category, existing)
  }

  return groups
}

/**
 * Format facet groups for the prompt (compact format like interviewer module)
 */
function formatFacetGroups(groups: Map<string, FacetTypeDefinition[]>): string {
  const lines: string[] = []

  const categoryLabels: Record<string, string> = {
    market_problems: 'MARKET PROBLEMS & PAINS',
    market_segments: 'MARKET SEGMENTS & STAKEHOLDERS',
    market_competition: 'COMPETITION & MARKET',
    business_strategy: 'BUSINESS STRATEGY',
    business_model: 'BUSINESS MODEL',
    business_risks: 'RISKS & WEAKNESSES',
    solution_features: 'SOLUTION & FEATURES',
    solution_constraints: 'CONSTRAINTS & DEPENDENCIES',
    solution_execution: 'EXECUTION & ACTIONS',
    insights_only: 'INSIGHTS (no entity)',
  }

  for (const [category, facets] of groups) {
    lines.push(`${categoryLabels[category] || category}:`)

    for (const facet of facets) {
      // Compact one-liner format like interviewer module
      const entityNote = facet.defaultEntityType ? ` → ${facet.defaultEntityType}` : ''
      lines.push(`- ${facet.id}: ${facet.description}${entityNote}`)
    }

    lines.push('')
  }

  return lines.join('\n')
}

/**
 * Get the default enabled facets (all enabled by default)
 */
export function getDefaultEnabledFacets(): FacetTypeDefinition[] {
  return EXPANDED_FACET_SCHEMA.filter(f => f.isEnabled)
}

/**
 * Get facets by IDs
 */
export function getFacetsByIds(ids: string[]): FacetTypeDefinition[] {
  const idSet = new Set(ids)
  return EXPANDED_FACET_SCHEMA.filter(f => idSet.has(f.id))
}
